import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#17479e";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`;

function MentorPackageDiscountDialog({ open, mentorRow, onClose, onSuccess }) {
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packageId, setPackageId] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPackageId("");
    setDiscountPercent("");
    setLoadingPackages(true);
    api
      .get("/admin/mentor-session-packages")
      .then((res) => {
        const raw = res.data?.data;
        setPackages(Array.isArray(raw) ? raw : (raw?.content ?? []));
      })
      .catch((err) => {
        console.error("Session packages fetch error:", err);
        toast.error("Failed to load packages.");
      })
      .finally(() => setLoadingPackages(false));
  }, [open]);

  const handleSubmit = async () => {
    if (!mentorRow || !packageId || discountPercent === "") return;
    setSaving(true);
    try {
      await api.put(
        `/admin/mentor-session-packages/discounts/${mentorRow.mentorId}`,
        {
          packageId: Number(packageId),
          discountPercent: Number(discountPercent),
        },
      );
      toast.success("Package discount updated successfully!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to update package discount. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalOfferIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
            >
              Update Package Discount
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
            >
              {mentorRow?.mentorName ?? "—"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
          <InputLabel>Package</InputLabel>
          {loadingPackages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Select
              label="Package"
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
            >
              {packages.map((pkg) => (
                <MenuItem key={pkg.packageId} value={pkg.packageId}>
                  {pkg.name}
                  {pkg.discountPercent != null
                    ? ` (current: ${pkg.discountPercent}%)`
                    : ""}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Discount Percent"
          placeholder="e.g. 20"
          value={discountPercent}
          onChange={(e) => setDiscountPercent(e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !packageId || discountPercent === ""}
          onClick={handleSubmit}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            color: "#fff !important",
            fontWeight: 700,
            px: 3,
            background: HEADER_GRADIENT,
            boxShadow: "none",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorPackageDiscountDialog;
